/**
 * Route registry. Marketing routes come from `marketing/routes.js` (owned by the marketing
 * work) when it exists; app routes are wrapped by the signed-in shell (auth guard, nav).
 * Order matters: first match wins.
 */
import { go } from './lib/nav.js';

/** @typedef {import('./router.js').Route} Route */

// Guarded: resolves to {} when the marketing module doesn't exist yet.
const marketingMods = import.meta.glob('./marketing/routes.js', { eager: true });
const mk = /** @type {any} */ (marketingMods['./marketing/routes.js']);
/** @type {Route[]} */
const marketingRoutes = mk?.routes ?? mk?.default ?? [];

const dealMods = import.meta.glob('./pages/app/deal/index.js');
const dealLoader =
  dealMods['./pages/app/deal/index.js'] ?? (() => import('./pages/app/deal-missing.js'));

/**
 * A signed-in route: lazily loads the shell + page, then renders through the shell.
 * @param {string} path
 * @param {() => Promise<any>} load
 * @param {{ chrome?: boolean, allowNotOnboarded?: boolean }} [opts]
 * @returns {Route}
 */
const app = (path, load, opts) => ({
  path,
  load: async () => {
    const [shell, mod] = await Promise.all([import('./components/shell.js'), load()]);
    return { render: (el, params) => shell.renderApp(el, params, mod, opts) };
  },
});

/** @param {string} path @param {string} to @returns {Route} */
const redirect = (path, to) => ({
  path,
  load: async () => ({
    render: () => go(to + window.location.search + window.location.hash, { replace: true }),
  }),
});

/** @type {Route[]} */
const authRoutes = [
  { path: '/start', load: () => import('./pages/auth/start.js') },
  { path: '/signin', load: () => import('./pages/auth/signin.js') },
  { path: '/auth/verify', load: () => import('./pages/auth/verify.js') },
  { path: '/auth', load: () => import('./pages/auth/verify.js') },
  { path: '/join', load: () => import('./pages/auth/join.js') },
  { path: '/invite', load: () => import('./pages/auth/join.js') },
  { path: '/signout', load: () => import('./pages/auth/signout.js') },
];

/** @type {Route[]} */
const appRoutes = [
  app('/app/welcome', () => import('./pages/app/onboarding.js'), {
    chrome: false,
    allowNotOnboarded: true,
  }),
  app('/app/deal', dealLoader, { chrome: false }),
  app('/app/cards/:cardId', () => import('./pages/app/cards.js')),
  app('/app/cards', () => import('./pages/app/cards.js')),
  app('/app/deck', () => import('./pages/app/deck.js')),
  app('/app/checkin/live', () => import('./pages/app/checkin-flow.js')),
  app('/app/checkin/:checkinId', () => import('./pages/app/checkin-detail.js')),
  app('/app/checkin', () => import('./pages/app/checkin.js')),
  app('/app/unicorn', () => import('./pages/app/unicorn.js')),
  app('/app/wild', () => import('./pages/app/wild.js')),
  app('/app/activity', () => import('./pages/app/activity.js')),
  app('/app/household', () => import('./pages/app/household.js')),
  app('/app', () => import('./pages/app/home.js')),
  redirect('/checkin', '/app/checkin'),
  redirect('/app/hands', '/app/cards'),
];

/** @type {Route[]} */
export const routes = [...authRoutes, ...appRoutes, ...marketingRoutes];

/** @type {Route} */
export const notFound = mk?.notFound ?? { path: '*', load: () => import('./pages/not-found.js') };
